import React, { useState, useRef, useEffect } from 'react';
import {
  Plus,
  ChevronDown,
  ChevronRight,
  MoreVertical,
  FolderOpen,
  MessageCircle,
  Pin,
  Edit3,
  Trash2,
  X,
  Check,
  Compass,
} from 'lucide-react';
import { Button, ConfirmModal } from '../../ui';
import styles from './ChatSidebar.module.css';

export interface ChatItem {
  id: string;
  title: string;
  assistantId?: string;
  isPinned?: boolean;
  lastMessageAt?: string;
}

export interface AssistantFolder {
  id: string;
  name: string;
  chats: ChatItem[];
  isExpanded?: boolean;
}

export interface AssistantItem {
  id: string;
  name: string;
  icon?: string;
}

export interface ChatSidebarProps {
  assistants: AssistantItem[];
  ungroupedChats: ChatItem[];
  assistantFolders: AssistantFolder[];
  selectedChatId?: string;
  selectedAssistantId?: string;
  onNewChat: () => void;
  onChatSelect: (chatId: string) => void;
  onAssistantSelect: (assistantId: string) => void;
  onExploreAssistants: () => void;
  onExploreWorkflows: () => void;
  onRenameChat: (chatId: string, newTitle: string) => void;
  onDeleteChat: (chatId: string) => void;
  onPinChat: (chatId: string) => void;
}

interface ContextMenuState {
  isOpen: boolean;
  chatId: string | null;
  x: number;
  y: number;
}

interface DeleteModalState {
  isOpen: boolean;
  chatId: string | null;
  chatTitle: string;
}

export const ChatSidebar: React.FC<ChatSidebarProps> = ({
  assistants,
  ungroupedChats,
  assistantFolders,
  selectedChatId,
  selectedAssistantId,
  onNewChat,
  onChatSelect,
  onAssistantSelect,
  onExploreAssistants,
  onExploreWorkflows,
  onRenameChat,
  onDeleteChat,
  onPinChat,
}) => {
  const [expandedSections, setExpandedSections] = useState({
    assistants: true,
    workflows: true,
    chats: true,
    folders: true,
  });
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [contextMenu, setContextMenu] = useState<ContextMenuState>({
    isOpen: false,
    chatId: null,
    x: 0,
    y: 0,
  });
  const [deleteModal, setDeleteModal] = useState<DeleteModalState>({
    isOpen: false,
    chatId: null,
    chatTitle: '',
  });
  const [editingChatId, setEditingChatId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const editInputRef = useRef<HTMLInputElement>(null);
  const contextMenuRef = useRef<HTMLDivElement>(null);

  // Auto-expand folders that have chats
  useEffect(() => {
    if (assistantFolders.length > 0) {
      const foldersWithChats = new Set(
        assistantFolders.filter(f => f.chats.length > 0).map(f => f.id)
      );
      setExpandedFolders(prev => {
        const newSet = new Set(prev);
        foldersWithChats.forEach(id => newSet.add(id));
        return newSet;
      });
    }
  }, [assistantFolders]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(e.target as Node)) {
        setContextMenu({ isOpen: false, chatId: null, x: 0, y: 0 });
      }
    };
    if (contextMenu.isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [contextMenu.isOpen]);

  useEffect(() => {
    if (editingChatId && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingChatId]);

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const toggleFolder = (folderId: string) => {
    setExpandedFolders((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(folderId)) {
        newSet.delete(folderId);
      } else {
        newSet.add(folderId);
      }
      return newSet;
    });
  };

  const handleContextMenu = (e: React.MouseEvent, chatId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ isOpen: true, chatId, x: e.clientX, y: e.clientY });
  };

  const handleMenuClick = (e: React.MouseEvent, chatId: string) => {
    e.stopPropagation();
    const rect = (e.target as HTMLElement).getBoundingClientRect();
    setContextMenu({ isOpen: true, chatId, x: rect.left, y: rect.bottom + 5 });
  };

  const handleRename = () => {
    if (contextMenu.chatId) {
      const chat = findChat(contextMenu.chatId);
      if (chat) {
        setEditTitle(chat.title);
        setEditingChatId(contextMenu.chatId);
      }
    }
    setContextMenu({ isOpen: false, chatId: null, x: 0, y: 0 });
  };

  const handleDelete = () => {
    if (contextMenu.chatId) {
      const chat = findChat(contextMenu.chatId);
      setDeleteModal({
        isOpen: true,
        chatId: contextMenu.chatId,
        chatTitle: chat?.title || 'this chat',
      });
    }
    setContextMenu({ isOpen: false, chatId: null, x: 0, y: 0 });
  };

  const handleConfirmDelete = () => {
    if (deleteModal.chatId) {
      onDeleteChat(deleteModal.chatId);
    }
    setDeleteModal({ isOpen: false, chatId: null, chatTitle: '' });
  };

  const handleCancelDelete = () => {
    setDeleteModal({ isOpen: false, chatId: null, chatTitle: '' });
  };

  const handlePin = () => {
    if (contextMenu.chatId) {
      onPinChat(contextMenu.chatId);
    }
    setContextMenu({ isOpen: false, chatId: null, x: 0, y: 0 });
  };

  const handleSaveRename = (chatId: string) => {
    if (editTitle.trim()) {
      onRenameChat(chatId, editTitle.trim());
    }
    setEditingChatId(null);
    setEditTitle('');
  };

  const handleCancelRename = () => {
    setEditingChatId(null);
    setEditTitle('');
  };

  const handleKeyDown = (e: React.KeyboardEvent, chatId: string) => {
    if (e.key === 'Enter') handleSaveRename(chatId);
    else if (e.key === 'Escape') handleCancelRename();
  };

  const findChat = (chatId: string): ChatItem | undefined => {
    const ungrouped = ungroupedChats.find((c) => c.id === chatId);
    if (ungrouped) return ungrouped;
    for (const folder of assistantFolders) {
      const chat = folder.chats.find((c) => c.id === chatId);
      if (chat) return chat;
    }
    return undefined;
  };

  const renderChatItem = (chat: ChatItem, isNested: boolean = false) => {
    const isEditing = editingChatId === chat.id;
    return (
      <div
        key={chat.id}
        className={`${styles.chatItem} ${isNested ? styles.nested : ''} ${selectedChatId === chat.id ? styles.active : ''} ${chat.isPinned ? styles.pinned : ''}`}
        onClick={() => !isEditing && onChatSelect(chat.id)}
        onContextMenu={(e) => handleContextMenu(e, chat.id)}
      >
        {chat.isPinned && <Pin size={12} className={styles.pinIcon} />}
        <MessageCircle size={14} className={styles.chatIcon} />
        {isEditing ? (
          <div className={styles.editContainer}>
            <input
              ref={editInputRef}
              type="text"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              onKeyDown={(e) => handleKeyDown(e, chat.id)}
              className={styles.editInput}
              onClick={(e) => e.stopPropagation()}
            />
            <button
              className={styles.editBtn}
              onClick={(e) => {
                e.stopPropagation();
                handleSaveRename(chat.id);
              }}
            >
              <Check size={14} />
            </button>
            <button
              className={styles.editBtn}
              onClick={(e) => {
                e.stopPropagation();
                handleCancelRename();
              }}
            >
              <X size={14} />
            </button>
          </div>
        ) : (
          <>
            <span className={styles.chatTitle}>{chat.title}</span>
            <button className={styles.menuButton} onClick={(e) => handleMenuClick(e, chat.id)}>
              <MoreVertical size={14} />
            </button>
          </>
        )}
      </div>
    );
  };

  return (
    <div className={styles.sidebar}>
      <div className={styles.header}>
        <h1 className={styles.title}>Chats</h1>
        <Button variant="primary" size="sm" leftIcon={<Plus size={16} />} onClick={onNewChat}>
          New Chat
        </Button>
      </div>

      <div className={styles.sections}>
        {/* Assistants Section */}
        <div className={styles.section}>
          <button className={styles.sectionHeader} onClick={() => toggleSection('assistants')}>
            {expandedSections.assistants ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            <span className={styles.sectionTitle}>ASSISTANTS</span>
          </button>
          {expandedSections.assistants && (
            <div className={styles.sectionContent}>
              {assistants.map((assistant) => (
                <button
                  key={assistant.id}
                  className={`${styles.item} ${selectedAssistantId === assistant.id ? styles.active : ''}`}
                  onClick={() => onAssistantSelect(assistant.id)}
                >
                  <span className={styles.assistantIcon}>{assistant.icon || '🤖'}</span>
                  <span className={styles.itemText}>{assistant.name}</span>
                </button>
              ))}
              <button className={styles.exploreLink} onClick={onExploreAssistants}>
                <Compass size={14} />
                <span>Explore Assistants</span>
              </button>
            </div>
          )}
        </div>

        {/* Workflows Section */}
        <div className={styles.section}>
          <button className={styles.sectionHeader} onClick={() => toggleSection('workflows')}>
            {expandedSections.workflows ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            <span className={styles.sectionTitle}>WORKFLOWS</span>
          </button>
          {expandedSections.workflows && (
            <div className={styles.sectionContent}>
              <button className={styles.exploreLink} onClick={onExploreWorkflows}>
                <Compass size={14} />
                <span>Explore Workflows</span>
              </button>
            </div>
          )}
        </div>

        {/* Ungrouped Chats Section */}
        <div className={styles.section}>
          <button className={styles.sectionHeader} onClick={() => toggleSection('chats')}>
            {expandedSections.chats ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            <span className={styles.sectionTitle}>CHATS</span>
          </button>
          {expandedSections.chats && (
            <div className={styles.sectionContent}>
              {ungroupedChats.length === 0 ? (
                <div className={styles.emptyState}>No chats yet</div>
              ) : (
                ungroupedChats.map((chat) => renderChatItem(chat))
              )}
            </div>
          )}
        </div>

        {/* Folders (Assistant Chats) Section */}
        <div className={styles.section}>
          <button className={styles.sectionHeader} onClick={() => toggleSection('folders')}>
            {expandedSections.folders ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            <span className={styles.sectionTitle}>FOLDERS</span>
          </button>
          {expandedSections.folders && (
            <div className={styles.sectionContent}>
              {assistantFolders.length === 0 ? (
                <div className={styles.emptyState}>No assistant chats yet</div>
              ) : (
                assistantFolders.map((folder) => (
                  <div key={folder.id} className={styles.folder}>
                    <button className={styles.folderHeader} onClick={() => toggleFolder(folder.id)}>
                      {expandedFolders.has(folder.id) ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      <FolderOpen size={14} className={styles.folderIcon} />
                      <span className={styles.folderName}>{folder.name}</span>
                      <span className={styles.chatCount}>{folder.chats.length}</span>
                    </button>
                    {expandedFolders.has(folder.id) && (
                      <div className={styles.folderContent}>
                        {folder.chats.length === 0 ? (
                          <div className={styles.emptyFolder}>No chats</div>
                        ) : (
                          folder.chats.map((chat) => renderChatItem(chat, true))
                        )}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* Context Menu */}
      {contextMenu.isOpen && (
        <div
          ref={contextMenuRef}
          className={styles.contextMenu}
          style={{ position: 'fixed', top: contextMenu.y, left: contextMenu.x }}
        >
          <button className={styles.contextMenuItem} onClick={handlePin}>
            <Pin size={14} />
            <span>{findChat(contextMenu.chatId || '')?.isPinned ? 'Unpin' : 'Pin'}</span>
          </button>
          <button className={styles.contextMenuItem} onClick={handleRename}>
            <Edit3 size={14} />
            <span>Rename</span>
          </button>
          <button className={`${styles.contextMenuItem} ${styles.danger}`} onClick={handleDelete}>
            <Trash2 size={14} />
            <span>Delete</span>
          </button>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete Chat"
        message={`Are you sure you want to delete "${deleteModal.chatTitle}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
};

export default ChatSidebar;
